import React, {
  useSyncExternalStore,
  useMemo,
  useState,
  PropsWithChildren,
} from 'react';
import { subscribeToAuthChanges, getUserToken } from '../utils/authentication';
import UserContext from './UserContext';
import {
  getUserAuth,
  INITIAL_USER_AUTH,
  UserAuthType,
} from '../utils/authentication';
import PurchaseContext from './PurchaseContext';
import agent from '../agent';
import { transformJobDetailParams } from './JobSearchContext';
import { JobDetailViewType } from '../components/job-postings/JobDetailLayout';

type UrlParamsType = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  discountCode?: string;
  jobId?: string;
  inviteQty?: string;
  jobQty?: string;
  distroQty?: string;
  nationalQty?: string;
  //for navigating to an application detail from an email link from hubspot:
  // format= dentalpost.net/dental-jobs/login/?fromPath=app/post-job/summary/{{custom.job_id}}&u={{custom.resume_encoded_username}}
  username?: string;
};

type LeadCaptureFieldsType = {
  email?: string;
  name?: string;
  phone?: string;
  resource?: string;
  leadSubscribeFlow?: boolean;
  position?: string;
  zip?: string;
};

export type GlobalContextType = {
  userAuth: UserAuthType;
  urlParams: UrlParamsType;
  updateUrlParams: (params: URLSearchParams) => void;
  resetUrlParams: (params: Partial<UrlParamsType>) => void;
  redirectPath: string | undefined;
  updateRedirectPath: (path?: string) => void;
  leadCaptureFields: LeadCaptureFieldsType;
  updateLeadCaptureFields: (props: LeadCaptureFieldsType) => void;
  jobToApply: JobDetailViewType | null;
  updateJobToApply: (properties: Partial<JobDetailViewType>) => void;
  resetJobToApply: () => void;
  fetchJobToApply: (jobId: number) => void;
  jobFetchError: string;
};

export const GlobalContext = React.createContext<GlobalContextType>({
  userAuth: INITIAL_USER_AUTH,
  urlParams: {},
  updateUrlParams: (params: URLSearchParams) => {},
  resetUrlParams: (params: Partial<UrlParamsType>) => {},
  redirectPath: undefined,
  updateRedirectPath: (path?: string) => {},
  leadCaptureFields: {},
  updateLeadCaptureFields: () => {},
  jobToApply: null,
  jobFetchError: '',
  fetchJobToApply: (jobId: number) => {},
  updateJobToApply: (properties: Partial<JobDetailViewType>) => {},
  resetJobToApply: () => {},
});

const GlobalContextWrapper = (props: PropsWithChildren) => {
  const userToken = useSyncExternalStore(
    subscribeToAuthChanges,
    getUserToken,
    () => null
  );
  const userAuth = useMemo(() => getUserAuth(userToken), [userToken]);
  const [urlParams, setUrlParams] = useState<UrlParamsType>({});
  const [leadCaptureFields, setLeadCaptureFields] =
    useState<LeadCaptureFieldsType>({});
  const [redirectPath, setRedirectPath] = useState<string | undefined>(
    undefined
  );

  const [jobToApply, setJobToApply] = useState<JobDetailViewType | null>(null);
  const [jobFetchError, setJobFetchError] = useState('');

  function updateRedirectPath(path?: string) {
    setRedirectPath(path);
  }

  function removeTrailingSlash(param?: string) {
    if (param?.endsWith('/')) {
      return param.slice(0, -1);
    } else return param;
  }

  function updateUrlParams(params: URLSearchParams) {
    //for private route links from emails or wordpress pages/events/blogs the url uses a
    //fromPath search parameter to redirect to after login.
    //i.e. dentalpost.net/login/?fromPath=salary-survey
    const fromPath = params.get('fromPath')?.replace('%2F', '/') || undefined;
    if (!!fromPath) {
      const updatedPath = fromPath.startsWith('/') ? fromPath : `/${fromPath}`;
      updateRedirectPath(updatedPath);
    }
    const discountCode =
      params.get('discount-code') || params.get('discountCode') || undefined;
    const utm_source = params.get('utm_source') || undefined;
    const utm_medium = params.get('utm_medium') || undefined;
    const utm_campaign = params.get('utm_campaign') || undefined;
    const utm_content = params.get('utm_content') || undefined;
    const username = params.get('u') || undefined;
    const jobId = params.get('jobId') || undefined;
    const inviteQty = params.get('inviteQty') || undefined;
    const jobQty = params.get('jobQty') || undefined;
    const distroQty = params.get('distroQty') || undefined;
    const nationalQty = params.get('nationalQty') || undefined;

    setUrlParams({
      discountCode: removeTrailingSlash(discountCode),
      utm_source: removeTrailingSlash(utm_source),
      utm_medium: removeTrailingSlash(utm_medium),
      utm_campaign: removeTrailingSlash(utm_campaign),
      utm_content: removeTrailingSlash(utm_content),
      username: removeTrailingSlash(username),
      jobId: removeTrailingSlash(jobId),
      inviteQty: removeTrailingSlash(inviteQty),
      jobQty: removeTrailingSlash(jobQty),
      distroQty: removeTrailingSlash(distroQty),
      nationalQty: removeTrailingSlash(nationalQty),
    });
  }

  function fetchJobToApply(jobId: number) {
    if (userAuth.isAuthenticated) {
      agent.Jobs.get({ jobId })
        .then((res) => {
          setJobToApply(transformJobDetailParams(res.data));
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            setJobFetchError('This job posting is no longer available');
          } else {
            setJobFetchError(err.message);
          }
        });
    } else {
      agent.Jobs.getPublic({ jobId })
        .then((res) => {
          setJobToApply(
            transformJobDetailParams(
              res.data.premiumJobs[0] || res.data.partnerJobs[0]
            )
          );
        })
        .catch((err) => {
          if (err.response?.status === 404) {
            setJobFetchError('This job posting is no longer available');
          } else {
            setJobFetchError(err.message);
          }
        });
    }
  }

  function updateJobToApply(properties: Partial<JobDetailViewType>) {
    if (!!jobToApply) {
      setJobToApply({ ...jobToApply, ...properties });
    }
  }

  function resetJobToApply() {
    setJobToApply(null);
  }

  function resetUrlParams(params: Partial<UrlParamsType>) {
    setUrlParams((prev) => ({ ...prev, ...params }));
  }

  function updateLeadCaptureFields(props: LeadCaptureFieldsType) {
    setLeadCaptureFields({ ...props });
  }

  //for DevTools to display context name
  GlobalContext.displayName = 'Global Context';

  return (
    <GlobalContext.Provider
      value={{
        userAuth,
        updateUrlParams,
        redirectPath,
        updateRedirectPath,
        urlParams: urlParams,
        resetUrlParams,
        leadCaptureFields,
        updateLeadCaptureFields,
        jobToApply,
        fetchJobToApply,
        jobFetchError,
        updateJobToApply,
        resetJobToApply,
      }}
    >
      <PurchaseContext>
        <UserContext>{props.children}</UserContext>
      </PurchaseContext>
    </GlobalContext.Provider>
  );
};

export default GlobalContextWrapper;
